import { TimePicker } from "@tql/ui-components-react/kendo/dateinputs";
import type { MaskedTextBoxEvent } from "@tql/ui-components-react/kendo/inputs";
import { Input, RadioButton } from "@tql/ui-components-react/kendo/inputs";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { DatePicker } from "@tql/ui-components-react/tql/dateinputs";
import {
	Checkbox,
	MaskedTextBox,
} from "@tql/ui-components-react/tql/inputs/beta";
import {
	bookOpen_01Line,
	pencil_01,
} from "@tql/ui-components-react/untitled/icons";
import { useAtom } from "jotai";
import { useState } from "react";

import { AddAddressModal } from "~/features/address-book/components/add-address-modal/add-address-modal.tsx";
import {
	APPOINTMENT_SERVICE_NAME,
	RESIDENTIAL_SERVICE_NAME,
} from "~/features/quote/components/quote-details/components/pickup-delivery-details/hooks/use-pickup-delivery-details.ts";
import { AddressBookModal } from "~/shared/components/address-book-modal/address-book-modal.tsx";
import type { Accessorial } from "~/shared/schemas/accessorial-schema.ts";
import type { AddressBookCreateBodyV2 } from "~/shared/schemas/address-book-schema.ts";
import type { ShipmentManagementPickupDeliveryDetails } from "~/shared/schemas/shipment-management-schema.ts";
import type { ApplyableAddress } from "~/shared/types/applyable-address-types.ts";
import type { StopName } from "~/shared/types/stop-types.ts";
import { formatPhoneNumber } from "~/shared/utils/string-fns.ts";
import {
	formatTimeToString,
	mergeDateTime,
	parseIsoDate,
	parseTimeString,
	toIsoString,
} from "~/shared/utils/time-utils.ts";

import { SHIPMENT_TOAST_MESSAGES } from "../../constants/toast-messages.ts";
import { useGetAccessorials } from "../../hooks/use-get-accessorials.ts";
import {
	useShipmentValidationErrors,
	useStopDraft,
} from "../../hooks/use-shipment-draft.ts";
import { useShipmentNotifications } from "../../hooks/use-shipment-notifications.ts";
import { stopSpecialInstructionsDraftAtom } from "../../state/shipment-edit-atoms.ts";
import { buildAddressFormInitialValues } from "../../utils/build-address-form-initial-values.ts";
import { buildStopFromAddress } from "../../utils/build-stop-from-address.ts";
import { combineCompanyAddress } from "../../utils/combine-company-address.ts";
import { PICKUP_DELIVERY_DETAILS_DEFAULT } from "../../utils/defaults.ts";
import { rebuildSpecialInstructions } from "../../utils/format-special-instructions.ts";

type ShipmentDetailEditProps = {
	readonly details?: ShipmentManagementPickupDeliveryDetails;
	readonly name: StopName;
	readonly customerId: number;
};

// New address book entries default to Commercial (as on the stop) so they always
// carry a detail for this stop type and appear in this stop's address book list.
const buildNewAddressInitialValues = (
	name: StopName,
	locationTypeOptions: Accessorial[],
): Record<string, unknown> => {
	const commercial = locationTypeOptions.find(
		(option) => option.name === "Commercial",
	);
	return { [`${name}_accessorials`]: commercial ? [commercial] : [] };
};

export const ShipmentDetailEdit = ({
	details = PICKUP_DELIVERY_DETAILS_DEFAULT,
	name,
	customerId,
}: ShipmentDetailEditProps) => {
	const label = name === "pickup" ? "Pickup" : "Delivery";
	const [showAddressBook, setShowAddressBook] = useState(false);
	// Which flavour of the add/edit address modal is open (null when closed).
	const [addressModalMode, setAddressModalMode] = useState<
		"create" | "edit" | null
	>(null);
	const { showSuccess } = useShipmentNotifications();

	const { data: accessorialData } = useGetAccessorials();
	// Show "Commercial" first, preserving the order of the remaining options.
	const locationTypeOptions = [
		...(accessorialData?.grouped.locationType[name] ?? []),
	].sort(
		(a, b) => Number(b.name === "Commercial") - Number(a.name === "Commercial"),
	);
	const accessorialOptions = accessorialData?.grouped.selectable[name] ?? [];

	const { stop: draftStop, updateStop, updateContact } = useStopDraft(name);
	const { updateStop: updatePickup } = useStopDraft("pickup");
	const [stopSpecialInstructions, setStopSpecialInstructions] = useAtom(
		stopSpecialInstructionsDraftAtom,
	);
	// Fall back to the passed-in details so the inputs still render their current
	// values before a draft has been seeded (e.g. isolated tests).
	const stop = draftStop ?? details;
	const isResidentialDelivery =
		name === "delivery" && stop.locationType === RESIDENTIAL_SERVICE_NAME;

	// Inline required-field validation errors for this stop, populated once the
	// user attempts to save and cleared live as fields are completed.
	const stopErrors = useShipmentValidationErrors()?.[name];
	const hasFetchedActualValue = Boolean(details.actual);

	const actualDate = parseIsoDate(stop.actual);

	const toggleAccessorial = (optionName: string) => {
		const current = stop.accessorials ?? [];
		updateStop({
			accessorials: current.includes(optionName)
				? current.filter((accessorial) => accessorial !== optionName)
				: [...current, optionName],
		});
	};

	const selectLocationType = (optionName: string) => {
		const nextAccessorials = stop.accessorials ?? [];
		const appointmentSelected = nextAccessorials.includes(
			APPOINTMENT_SERVICE_NAME,
		);
		const selectingResidential =
			name === "delivery" && optionName === RESIDENTIAL_SERVICE_NAME;
		const accessorialsWithoutAppointment =
			name === "delivery" && isResidentialDelivery && !selectingResidential
				? nextAccessorials.filter(
						(accessorial) => accessorial !== APPOINTMENT_SERVICE_NAME,
					)
				: nextAccessorials;
		updateStop({
			locationType: optionName,
			accessorials:
				selectingResidential && !appointmentSelected
					? [...accessorialsWithoutAppointment, APPOINTMENT_SERVICE_NAME]
					: accessorialsWithoutAppointment,
		});
	};

	// Populate the editable fields from a selected saved address. The values are
	// copied onto the stop draft only -- the address book item itself is never
	// updated from here. Only address book picks rebuild the merged special
	// instructions box from both stops' address instructions, pickup first.
	const applyAddress = (
		address: ApplyableAddress,
		shouldRebuildInstructions: boolean,
	) => {
		const { stop: stopPatch, contact } = buildStopFromAddress({
			address,
			name,
			stop,
			locationTypeOptions,
			accessorialOptions,
		});

		const { stopInstructions: nextStopInstructions, merged } =
			rebuildSpecialInstructions(
				stopSpecialInstructions,
				name,
				stopPatch.specialInstructions,
			);
		setStopSpecialInstructions(nextStopInstructions);

		/* eslint-disable @typescript-eslint/no-unused-vars, sonarjs/no-unused-vars -- rest siblings strip specialInstructions from the patch */
		const { specialInstructions: _specialInstructions, ...addressPatch } =
			stopPatch;
		/* eslint-enable @typescript-eslint/no-unused-vars, sonarjs/no-unused-vars -- re-enable after the omit destructure */
		updateContact(contact);
		if (!shouldRebuildInstructions) {
			updateStop(addressPatch);
			return;
		}
		updateStop(
			name === "delivery"
				? {
						...addressPatch,
						specialInstructions: nextStopInstructions.delivery || null,
					}
				: addressPatch,
		);
		updatePickup({ specialInstructions: merged });
	};

	// Edit Address only changes the stop's address; its location type,
	// accessorials, hours, contact, and special instructions are left as is.
	const applyEditedAddress = (address: ApplyableAddress) => {
		const { stop: stopPatch } = buildStopFromAddress({
			address,
			name,
			stop,
			locationTypeOptions,
			accessorialOptions,
		});
		updateStop({
			companyName: stopPatch.companyName,
			address: stopPatch.address,
			address1: stopPatch.address1,
			address2: stopPatch.address2,
			city: stopPatch.city,
			stateProvince: stopPatch.stateProvince,
			country: stopPatch.country,
			postalCode: stopPatch.postalCode,
		});
	};

	// Precompute the add/edit modal props to keep the JSX free of nested ternaries.
	const isEditingAddress = addressModalMode === "edit";
	const companyLocation = combineCompanyAddress(stop);
	const addressModalInitialValues = isEditingAddress
		? buildAddressFormInitialValues({
				name,
				stop: {
					...stop,
					specialInstructions:
						stopSpecialInstructions?.[name] ?? stop.specialInstructions,
				},
				locationTypeOptions,
				accessorialOptions,
			})
		: undefined;
	const newAddressInitialValues = buildNewAddressInitialValues(
		name,
		locationTypeOptions,
	);

	// Adding an address saves it to the address book, then returns the user to
	// the list so they can select the new entry.
	const returnToAddressBook = () => {
		setAddressModalMode(null);
		setShowAddressBook(true);
	};

	return (
		<>
			<div className={"shipment-stop shipment-detail-edit"}>
				<div className={"details"}>
					<div className={"company-details"}>
						<p className={"section-label"}>
							{label} Location
							<span className={"required-asterisk"}>*</span>
						</p>
						<Input
							aria-label={`${label} Company Name`}
							className={"company-location-input"}
							readOnly={true}
							title={companyLocation}
							value={companyLocation}
						/>
						{stopErrors?.companyName ? (
							<p className={"validation-error"}>{stopErrors.companyName}</p>
						) : null}
						<div className={"address-book-actions"}>
							<Button
								onClick={() => setShowAddressBook(true)}
								svgIcon={bookOpen_01Line}
								variant={"tertiary"}
							>
								Address Book
							</Button>
							<Button
								onClick={() => setAddressModalMode("edit")}
								svgIcon={pencil_01}
								variant={"tertiary"}
							>
								Edit Address
							</Button>
						</div>
					</div>
					<div className={"location-type"}>
						<p className={"section-label"}>
							Location Type<span className={"required-asterisk"}>*</span>
						</p>
						<div className={"location-type-options"}>
							{locationTypeOptions.map((option) => (
								<div key={option.id} className={"edit-option"}>
									<RadioButton
										checked={stop.locationType === option.name}
										name={`${name}-location-type`}
										onChange={() => selectLocationType(option.name)}
									/>
									<p>{option.name}</p>
								</div>
							))}
						</div>
						{stopErrors?.locationType ? (
							<p className={"validation-error"}>{stopErrors.locationType}</p>
						) : null}
					</div>
					<div className={"accessorials"}>
						<p className={"section-label"}>Accessorials</p>
						<div className={"accessorial-options"}>
							{accessorialOptions.map((option) => (
								<div key={option.id} className={"edit-option"}>
									<Checkbox
										checked={stop.accessorials?.includes(option.name) ?? false}
										disabled={Boolean(
											isResidentialDelivery &&
											option.name === APPOINTMENT_SERVICE_NAME,
										)}
										onChange={() => toggleAccessorial(option.name)}
									/>
									<p>{option.name}</p>
								</div>
							))}
						</div>
					</div>
					<div className={"contact-details"}>
						<div className={"contact-row contact-row-labels"}>
							<p className={"section-label"}>Contact Name</p>
							<p className={"section-label"}>
								Phone
								{name === "delivery" ? (
									<span className={"required-asterisk"}>*</span>
								) : null}
							</p>
							<p className={"section-label"}>EXT</p>
						</div>
						<div className={"contact-row contact-row-values"}>
							<Input
								aria-label={"Contact Name"}
								onChange={(event) =>
									updateContact({ name: String(event.value ?? "") })
								}
								value={stop.contact?.name ?? ""}
							/>
							<MaskedTextBox
								aria-label={"Phone"}
								mask={"(000) 000-0000"}
								onChange={(event: MaskedTextBoxEvent) =>
									updateContact({ phone: event.target.value })
								}
								placeholder={"(___) ___-____"}
								valid={!stopErrors?.phone}
								value={formatPhoneNumber(stop.contact?.phone ?? "")}
							/>
							<Input
								aria-label={"EXT"}
								onChange={(event) =>
									updateContact({ extension: String(event.value ?? "") })
								}
								value={stop.contact?.extension ?? ""}
							/>
						</div>
						{stopErrors?.phone ? (
							<div className={"contact-row contact-row-values"}>
								<p />
								<p className={"validation-error"}>{stopErrors.phone}</p>
								<p />
							</div>
						) : null}
					</div>
					<div className={"hours-details"}>
						{stop.appointmentType === "FCFS" ? (
							<div className={"hours-label-row"}>
								<p className={"section-label"}>{label} Hours</p>
								<span className={"hours-appointment-type"}>FCFS</span>
							</div>
						) : null}
						<div className={"scheduled-actual-details"}>
							<div>
								<p className={"section-label"}>
									{label} Hours From
									<span className={"required-asterisk"}>*</span>
								</p>
								<TimePicker
									onChange={(event) =>
										updateStop({
											shipmentHoursFrom: formatTimeToString(event.value),
										})
									}
									valid={!stopErrors?.shipmentHoursFrom}
									value={parseTimeString(stop.shipmentHoursFrom)}
								/>
								{stopErrors?.shipmentHoursFrom ? (
									<p className={"validation-error"}>
										{stopErrors.shipmentHoursFrom}
									</p>
								) : null}
							</div>
							<div>
								<p className={"section-label"}>
									{label} Hours To<span className={"required-asterisk"}>*</span>
								</p>
								<TimePicker
									onChange={(event) =>
										updateStop({
											shipmentHoursTo: formatTimeToString(event.value),
										})
									}
									valid={!stopErrors?.shipmentHoursTo}
									value={parseTimeString(stop.shipmentHoursTo)}
								/>
								{stopErrors?.shipmentHoursTo ? (
									<p className={"validation-error"}>
										{stopErrors.shipmentHoursTo}
									</p>
								) : null}
							</div>
						</div>
					</div>
					<div className={"scheduled-actual-details"}>
						<div>
							<p className={"section-label"}>
								Scheduled<span className={"required-asterisk"}>*</span>
							</p>
							<DatePicker
								onChange={(event) =>
									updateStop({ scheduled: toIsoString(event.value) })
								}
								valid={!stopErrors?.scheduled}
								value={parseIsoDate(stop.scheduled)}
							/>
							{stopErrors?.scheduled ? (
								<p className={"validation-error"}>{stopErrors.scheduled}</p>
							) : null}
						</div>
						<div>
							<p className={"section-label"}>Actual</p>
							{hasFetchedActualValue ? (
								<div
									className={"actual-date-time"}
									data-testid={`${name}-actual-date-time-inputs`}
								>
									<DatePicker
										data-testid={`${name}-actual-date-input`}
										onChange={(event) =>
											updateStop({
												actual: toIsoString(
													mergeDateTime(event.value, actualDate),
												),
											})
										}
										value={actualDate}
									/>
									<TimePicker
										data-testid={`${name}-actual-time-input`}
										onChange={(event) =>
											updateStop({
												actual: toIsoString(
													mergeDateTime(actualDate, event.value),
												),
											})
										}
										value={actualDate}
									/>
								</div>
							) : (
								<p>N/A</p>
							)}
						</div>
					</div>
					<div className={"scheduled-actual-details"}>
						<div>
							<p className={"section-label"}>{label} #</p>
							<Input
								aria-label={`${label} Number`}
								onChange={(event) =>
									updateStop({ shipmentNumber: String(event.value ?? "") })
								}
								value={stop.shipmentNumber ?? ""}
							/>
						</div>
					</div>
				</div>
			</div>
			{showAddressBook ? (
				<AddressBookModal
					customerId={customerId}
					name={name}
					onAddNew={() => {
						setShowAddressBook(false);
						setAddressModalMode("create");
					}}
					onClose={() => setShowAddressBook(false)}
					onSelect={(address) => {
						applyAddress(address, true);
						setShowAddressBook(false);
					}}
				/>
			) : null}
			{addressModalMode === "create" ? (
				<AddAddressModal
					customerId={customerId}
					initialValues={newAddressInitialValues}
					onClose={returnToAddressBook}
					onCreated={returnToAddressBook}
					stopType={name}
					variant={"shipment"}
				/>
			) : null}
			{isEditingAddress ? (
				<AddAddressModal
					customerId={customerId}
					initialValues={addressModalInitialValues}
					isEdit={true}
					onClose={() => setAddressModalMode(null)}
					onSubmitDraft={(body: AddressBookCreateBodyV2) => {
						applyEditedAddress(body);
						showSuccess(SHIPMENT_TOAST_MESSAGES.addressUpdated);
					}}
					variant={"shipment"}
				/>
			) : null}
		</>
	);
};
